import React, { useCallback, useEffect, useRef, useState } from 'react';
import { StyleSheet, View, StyleProp, ViewStyle } from 'react-native';
import ReactCrop, { Crop } from 'react-image-crop';
import { MB_CustomHTMLImageElement, MB_customWindowForWeb } from '@/utils/general/custom-window-for-web';
import { MB_Modal } from '@/components/general/modal';
import { MB_Button } from '@/components/general/button';
import { MB_ImageCropperConstants } from './MB_ImageCropperConstants';
import { MB_SVG_ImageTransparentBackgroundCheckers } from '@/components/general/svg-components';

// TODO: Stuck on version "^9.0.4",

interface IImageCropperForWeb {
  /**
   * Show the modal.
   */
  isVisible: boolean;

  /**
   * The blob of the image to crop.
   */
  imageToCrop?: Blob;

  /**
   * Callback when the crop finishes.
   */
  onResultReady: ({ croppedImage, url }: { croppedImage: Blob; url: string }) => void;

  /**
   * Callback when an error occurs.
   */
  onError: (error: Error) => void;

  /**
   * Callback when the modal is dismissed.
   */
  onDismiss: () => void;

  /**
   * If true, the crop will be flexible. This means that the crop will not be constrained to the aspect ratio, x and y values.
   */
  flexibleCrop?: boolean;

  /**
   * Custom crop values. If not provided, the default crop values will be used.
   */
  customCrop?: Partial<Crop>;

  buttonStyle?: StyleProp<ViewStyle>;

  buttonTextStyle?: StyleProp<ViewStyle>;
  /**
   * pass here borderBottom properties to override default style
   */
  modalTitleWrapperStyle?: StyleProp<ViewStyle>;
  /**
   * font and color of modal title
   */
  modalTitleStyle?: StyleProp<ViewStyle>;

  modalCloseButtonXProps?: { size?: number; color?: string };
  /**
   * use this to control background color of modal, paddings, etc
   */
  modalChildrenWrapperStyle?: StyleProp<ViewStyle>;
}

const MB_ImageCropperPopUpView = ({
  isVisible,
  imageToCrop,
  onResultReady,
  onError,
  onDismiss,
  flexibleCrop,
  customCrop,
  buttonTextStyle,
  modalTitleStyle,
  modalTitleWrapperStyle,
  modalCloseButtonXProps,
  modalChildrenWrapperStyle,
  buttonStyle,
}: IImageCropperForWeb) => {
  const defaultCropProps: Partial<Crop> = { unit: '%', height: 94, aspect: 1, x: 3, y: 3 };
  const flexibleCropProps: Partial<Crop> = { unit: '%', x: 400, y: 200 };

  const [crop, setCrop] = useState<Partial<Crop>>({ ...(!flexibleCrop ? flexibleCropProps : defaultCropProps), ...customCrop });

  const imgRef = useRef<MB_CustomHTMLImageElement | null>(null);
  const [completedCrop, setCompletedCrop] = useState<Partial<Crop>>({});
  const [upImg, setUpImg] = useState<string | null>();
  const [isCropping, setIsCropping] = useState(false);
  const [isCropInitialized, setIsCropInitialized] = useState(false);

  // Centers the crop vertically or horizontally depending on image dimensions
  useEffect(() => {
    if (crop.unit === 'px' && crop.width && crop.height && !isCropInitialized && imgRef !== null && imgRef.current !== null) {
      const imageWidth = imgRef.current.width;
      const imageHeight = imgRef.current.height;
      const cropWidth = crop.width;
      const cropHeight = crop.height;

      const newCrop = crop;
      newCrop.x = imageWidth / 2 - cropWidth / 2;
      newCrop.y = imageHeight / 2 - cropHeight / 2;

      setCrop(newCrop);
      setCompletedCrop(newCrop);
      setIsCropInitialized(true);
    }
  }, [crop, isCropInitialized]);

  const onLoad = useCallback((img: MB_CustomHTMLImageElement) => {
    imgRef.current = img;
  }, []);

  useEffect(() => {
    if (imageToCrop) {
      const reader = new FileReader();
      reader.addEventListener('load', () => setUpImg(reader.result?.toString()));
      reader.readAsDataURL(imageToCrop);
    }
  }, [imageToCrop]);

  if (!isVisible) {
    return null;
  }

  if (!imageToCrop) {
    setTimeout(() => onError(new Error('imageToCrop was undefined in ImageCropper')), 0);
    return null;
  }

  const canvasToBlob = (blob: Blob | null) => {
    setIsCropping(false);

    if (!blob) {
      onError(new Error('blob was null in canvasToBlob'));
      return;
    }

    onResultReady({ croppedImage: blob, url: URL.createObjectURL(blob) });
  };

  const onCropImagePressed = () => {
    setIsCropping(true);

    const canvas = MB_customWindowForWeb.document.createElement('canvas');

    if (!completedCrop || !imgRef.current) {
      return;
    }

    const image = imgRef.current;
    const innecrCrop = completedCrop;

    if (!innecrCrop || innecrCrop.x === undefined || innecrCrop.y === undefined || innecrCrop.width === undefined || innecrCrop.height === undefined) {
      return;
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return;
    }

    const pixelRatio = MB_customWindowForWeb.devicePixelRatio;

    canvas.width = innecrCrop.width * pixelRatio * scaleX;
    canvas.height = innecrCrop.height * pixelRatio * scaleY;

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';

    ctx.drawImage(
      image as HTMLImageElement,
      innecrCrop.x * scaleX,
      innecrCrop.y * scaleY,
      innecrCrop.width * scaleX,
      innecrCrop.height * scaleY,
      0,
      0,
      innecrCrop.width * scaleX,
      innecrCrop.height * scaleY
    );

    canvas.toBlob(canvasToBlob, imageToCrop.type, 1);
  };

  const getCrop = () => {
    const newCrop = crop;
    if (newCrop.width !== undefined) {
      newCrop.width = Math.max(newCrop.width, 50);
    }

    if (newCrop.height !== undefined) {
      newCrop.height = Math.max(newCrop.height, 50);
    }
    return newCrop;
  };

  return (
    <MB_Modal
      isVisible={isVisible}
      onDismiss={onDismiss}
      title={'Crop your image'}
      titleStyle={modalTitleStyle}
      titleWrapperStyle={modalTitleWrapperStyle}
      closeButtonXProps={modalCloseButtonXProps}
      childrenWrapperStyle={modalChildrenWrapperStyle}
    >
      <View>
        <MB_SVG_ImageTransparentBackgroundCheckers containerStyle={{ width: '100%', height: '100%', position: 'absolute' }} />
        <ReactCrop
          imageStyle={{ height: 512, objectFit: 'cover' }}
          onImageLoaded={image => onLoad(image as MB_CustomHTMLImageElement)}
          style={{ height: 512, width: 'auto' }}
          src={upImg ?? ''}
          crop={getCrop()}
          onChange={setCrop}
          onComplete={setCompletedCrop}
          minWidth={5}
          minHeight={5}
          ruleOfThirds
        />
      </View>

      <MB_Button
        title="Crop Image"
        onPress={onCropImagePressed}
        disabled={isCropping}
        loading={isCropping}
        style={[buttonStyle ?? styles.cropButton, styles.cropButtonMargin]}
        textStyle={buttonTextStyle}
      />
    </MB_Modal>
  );
};

export { MB_ImageCropperPopUpView };

const styles = StyleSheet.create({
  cropButton: {
    width: 268,
    height: 62,
    backgroundColor: MB_ImageCropperConstants.COLOR.plum,
    opacity: 1,
  },
  cropButtonMargin: {
    marginTop: 32,
  },
});
